import {
    EventClient,
    EventClientProvider,
    FormattedEvent,
    FormattedEventResponse,
} from '@eb/event-renderer';
import React, { useEffect, useState } from 'react';
import { AppProps } from '../../types';
import { getShareEventComponentProps } from '../ShareEvent/ShareEventRoot';
import { ShareEventModalComponentProps } from '../ShareEventModal/ShareEventModal';
import { dynaMount } from '../utils/mountComponent';
import {
    getOrganizerRelatedEventsFromDestination,
    getRelatedEvents,
} from './api';
import MoreEventsFromThisOrganizer from './MoreEventsFromThisOrganizer';
import OtherEventsYouMayLike from './OtherEventsYouMayLike';

const eventClient = new EventClient();

export interface RelatedEventsProps {
    eventId: string;
    isAuthenticated: boolean;
    shareEventComponentProps: ShareEventModalComponentProps;
    events?: FormattedEvent[];
    organizerId: string;
}

export type RelatedEventsChildrenProps = Omit<
    RelatedEventsProps,
    'organizerId' | 'eventId'
>;

export const RELATED_EVENTS_ID = 'listings-root__related-events';
export const MORE_EVENTS_FROM_THIS_ORGANIZER_ID =
    'listings-root__more-events-from-this-organizer';

export const getRelatedEventsProps = (props: AppProps): RelatedEventsProps => {
    const {
        event: { id: eventId },
        user: { isAuthenticated },
        organizer: { id: organizerId },
    } = props;

    const shareEventModalProps = getShareEventComponentProps(props);

    return {
        eventId,
        isAuthenticated,
        shareEventComponentProps: shareEventModalProps,
        organizerId,
    };
};

export const OtherEventsYouMayLikeMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: OtherEventsYouMayLike,
            containerId: RELATED_EVENTS_ID,
        },
        { ...props },
    );

export const MoreEventsFromThisOrganizerMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: MoreEventsFromThisOrganizer,
            containerId: MORE_EVENTS_FROM_THIS_ORGANIZER_ID,
        },
        { ...props },
    );

export const RelatedEventsWrapper = (
    props: RelatedEventsProps,
): JSX.Element | null => {
    const [relatedEvents, setRelatedEvents] = useState<FormattedEvent[]>([]);
    const [
        relatedEventsSameOrganizer,
        setRelatedEventsSameOrganizer,
    ] = useState<FormattedEvent[]>([]);
    const { organizerId, eventId } = props;

    useEffect(() => {
        getOrganizerRelatedEventsFromDestination(organizerId, eventId).then(
            (response: FormattedEventResponse | undefined) => {
                let organizerEvents: string[] = [];
                if (response) {
                    organizerEvents = response.events.map((event) => event.id);
                    setRelatedEventsSameOrganizer(response.events);
                }
                getRelatedEvents(eventId).then((dataFromApi) => {
                    const filteredEvents =
                        organizerEvents.length > 0
                            ? dataFromApi.events.filter(
                                  (event) =>
                                      !organizerEvents.includes(event.id),
                              )
                            : dataFromApi.events;
                    setRelatedEvents(filteredEvents);
                });
            },
        );
    }, [eventId, organizerId]);

    if (relatedEvents.length === 0) {
        return <div data-testid="loading-state"></div>;
    }

    return (
        <EventClientProvider client={eventClient}>
            <div data-testid="related-events-wrapper">
                <OtherEventsYouMayLikeMount {...props} events={relatedEvents} />
                <MoreEventsFromThisOrganizerMount
                    {...props}
                    events={relatedEventsSameOrganizer}
                />
            </div>
        </EventClientProvider>
    );
};

const RelatedEventsRoot = (props: AppProps): JSX.Element | null => {
    if (
        !document.getElementById(MORE_EVENTS_FROM_THIS_ORGANIZER_ID) &&
        !document.getElementById(RELATED_EVENTS_ID)
    ) {
        return <div data-testid="loading-state"></div>;
    }
    const relatedEventsProps = getRelatedEventsProps(props);
    return <RelatedEventsWrapper {...relatedEventsProps} />;
};

export default RelatedEventsRoot;
